import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import {
  ConfirmationLanguageCode,
  ConfirmationSupport,
  ConfirmationTemplate,
  ConfirmationTranslation,
  createDefaultConfirmationTemplate,
} from '../../types/RegistrationConfirmation.type';

type ConfirmationTemplateEditorProps = {
  value: ConfirmationTemplate;
  onChange: (value: ConfirmationTemplate) => void;
  // When true, drops the outer card/heading so the editor sits flush as its own
  // page (used by the stepped SubSubtype dialog). Defaults to the standalone card.
  embedded?: boolean;
};

const EDITABLE_LANGUAGES: Array<{
  code: ConfirmationLanguageCode;
  label: string;
}> = [
  { code: 'vi', label: 'Vietnamese' },
  { code: 'en', label: 'English' },
];

const ConfirmationTemplateEditor: React.FC<ConfirmationTemplateEditorProps> = ({
  value,
  onChange,
  embedded = false,
}) => {
  const fallbackTemplate = useMemo(
    () => createDefaultConfirmationTemplate(),
    [],
  );

  const getTranslation = (
    language: ConfirmationLanguageCode,
  ): ConfirmationTranslation =>
    value.translations[language] || fallbackTemplate.translations[language];

  const updateTranslation = (
    language: ConfirmationLanguageCode,
    patch: Partial<ConfirmationTranslation>,
  ) => {
    const translation = getTranslation(language);
    onChange({
      ...value,
      translations: {
        ...value.translations,
        [language]: {
          ...translation,
          ...patch,
        },
      },
    });
  };

  const updateSupport = (
    language: ConfirmationLanguageCode,
    key: keyof ConfirmationSupport,
    nextValue: string,
  ) => {
    const translation = getTranslation(language);
    updateTranslation(language, {
      support: {
        ...translation.support,
        [key]: nextValue,
      },
    });
  };

  const updateTerm = (
    language: ConfirmationLanguageCode,
    index: number,
    nextValue: string,
  ) => {
    const translation = getTranslation(language);
    const terms = [...translation.terms];
    terms[index] = nextValue;
    updateTranslation(language, { terms });
  };

  const addTerm = (language: ConfirmationLanguageCode) => {
    const translation = getTranslation(language);
    updateTranslation(language, { terms: [...translation.terms, ''] });
  };

  const removeTerm = (language: ConfirmationLanguageCode, index: number) => {
    const translation = getTranslation(language);
    updateTranslation(language, {
      terms: translation.terms.filter((_, termIndex) => termIndex !== index),
    });
  };

  const moveTerm = (
    language: ConfirmationLanguageCode,
    index: number,
    direction: -1 | 1,
  ) => {
    const translation = getTranslation(language);
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= translation.terms.length) return;

    const terms = [...translation.terms];
    [terms[index], terms[nextIndex]] = [terms[nextIndex], terms[index]];
    updateTranslation(language, { terms });
  };

  const renderTermsEditor = (
    language: ConfirmationLanguageCode,
    languageLabel: string,
  ) => {
    const translation = getTranslation(language);

    return (
      <Paper key={language} variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          <Typography variant="subtitle1" fontWeight="bold">
            {languageLabel}
          </Typography>
          {translation.terms.map((term, index) => (
            <Stack
              key={`${language}-term-${index}`}
              direction="row"
              alignItems="flex-start"
              spacing={1}
            >
              <Typography sx={{ pt: 1, minWidth: 24 }}>{index + 1}.</Typography>
              <TextField
                value={term}
                onChange={(event) =>
                  updateTerm(language, index, event.target.value)
                }
                placeholder={`Enter ${languageLabel.toLowerCase()} term`}
                size="small"
                fullWidth
                multiline
                minRows={1}
              />
              <Tooltip title="Move term up">
                <span>
                  <IconButton
                    type="button"
                    size="small"
                    onClick={() => moveTerm(language, index, -1)}
                    disabled={index === 0}
                    aria-label={`Move ${languageLabel} term up`}
                  >
                    <ArrowUp size={16} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Move term down">
                <span>
                  <IconButton
                    type="button"
                    size="small"
                    onClick={() => moveTerm(language, index, 1)}
                    disabled={index === translation.terms.length - 1}
                    aria-label={`Move ${languageLabel} term down`}
                  >
                    <ArrowDown size={16} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Remove term">
                <IconButton
                  type="button"
                  size="small"
                  onClick={() => removeTerm(language, index)}
                  aria-label={`Remove ${languageLabel} term`}
                  color="error"
                >
                  <Trash2 size={16} />
                </IconButton>
              </Tooltip>
            </Stack>
          ))}
          {translation.terms.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No terms configured for {languageLabel}.
            </Typography>
          )}
          <Box>
            <Button
              type="button"
              size="small"
              variant="outlined"
              startIcon={<Plus size={16} />}
              onClick={() => addTerm(language)}
            >
              Add {languageLabel} term
            </Button>
          </Box>
        </Stack>
      </Paper>
    );
  };

  const renderSupportEditor = (
    language: ConfirmationLanguageCode,
    languageLabel: string,
  ) => {
    const translation = getTranslation(language);

    return (
      <Paper key={language} variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          <Typography variant="subtitle1" fontWeight="bold">
            {languageLabel}
          </Typography>
          <TextField
            label="Support label"
            value={translation.support.label}
            onChange={(event) =>
              updateSupport(language, 'label', event.target.value)
            }
            size="small"
            fullWidth
          />
          <TextField
            label="Support channel"
            value={translation.support.channel}
            onChange={(event) =>
              updateSupport(language, 'channel', event.target.value)
            }
            size="small"
            fullWidth
          />
          <TextField
            label="Support phone"
            value={translation.support.phone}
            onChange={(event) =>
              updateSupport(language, 'phone', event.target.value)
            }
            size="small"
            fullWidth
          />
          <TextField
            label="Working hours"
            value={translation.support.workingHours}
            onChange={(event) =>
              updateSupport(language, 'workingHours', event.target.value)
            }
            size="small"
            fullWidth
          />
          <TextField
            label="Additional support text"
            value={translation.support.additionalText}
            onChange={(event) =>
              updateSupport(language, 'additionalText', event.target.value)
            }
            size="small"
            fullWidth
            multiline
            minRows={2}
          />
        </Stack>
      </Paper>
    );
  };

  const content = (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h6" fontWeight="bold">
          Registration Confirmation Template
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Enter the confirmation terms and support information in both
          Vietnamese and English. Customer, product, title, and field-label
          values are managed separately by the system.
        </Typography>
      </Box>

      <Divider />

      <Box>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          Terms and conditions
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'repeat(2, minmax(0, 1fr))',
            },
            gap: 2,
            alignItems: 'start',
          }}
        >
          {EDITABLE_LANGUAGES.map(({ code, label }) =>
            renderTermsEditor(code, label),
          )}
        </Box>
      </Box>

      <Box>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          Support information
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'repeat(2, minmax(0, 1fr))',
            },
            gap: 2,
            alignItems: 'start',
          }}
        >
          {EDITABLE_LANGUAGES.map(({ code, label }) =>
            renderSupportEditor(code, label),
          )}
        </Box>
      </Box>
    </Stack>
  );

  // Embedded: flush layout for use as its own wizard page. Standalone: wrapped
  // in the outlined card with top margin.
  if (embedded) {
    return content;
  }

  return (
    <Paper
      variant="outlined"
      sx={{ mt: 3, p: 2, backgroundColor: 'background.default' }}
    >
      {content}
    </Paper>
  );
};

export default ConfirmationTemplateEditor;
